import type {
  CampaignActivateApiData,
  CampaignActivateApiResponses,
  CampaignActivityListApiData,
  CampaignActivityListApiResponses,
  CampaignAssignApiData,
  CampaignAssignApiResponses,
  CampaignBrandListApiData,
  CampaignBrandListApiResponses,
  CampaignCreateApiData,
  CampaignCreateApiResponses,
  CampaignCustomerListApiData,
  CampaignCustomerListApiResponses,
  CampaignCustomerRecordList,
  CampaignDiscountOverlapsApiData,
  CampaignDiscountOverlapsApiResponses,
  CampaignGetApiData,
  CampaignGetApiResponses,
  CampaignListApiData,
  CampaignListApiResponses,
  CarWheelMeasurementMinimumListApiData,
  CarWheelMeasurementMinimumListApiResponses,
  CouponActivityListApiData,
  CouponActivityListApiResponses,
  CouponGroupCreateApiData,
  CouponGroupCreateApiResponses,
  CouponGroupGetApiData,
  CouponGroupGetApiResponses,
  CouponGroupListApiData,
  CouponGroupListApiResponses,
  CouponGroupPatchApiData,
  CouponGroupPatchApiResponses,
  CouponTemplateCreateApiData,
  CouponTemplateCreateApiResponses,
  CouponTemplateGetApiData,
  CouponTemplateGetApiResponses,
  CouponTemplateListApiData,
  CouponTemplateListApiResponses,
  CouponTemplatePatchApiData,
  CouponTemplatePatchApiResponses,
  LeadGenerationBonusApprovalCreateApiData,
  LeadGenerationBonusApprovalCreateApiResponses,
  LeadGenerationCreateApiData,
  LeadGenerationCreateApiResponses,
  LeadGenerationGetApiData,
  LeadGenerationGetApiResponses,
  LeadGenerationImageCreateApiData,
  LeadGenerationImageCreateApiResponses,
  LeadGenerationListApiData,
  LeadGenerationListApiResponses,
  LeadGenerationStatusEventCreateApiData,
  LeadGenerationStatusEventCreateApiResponses,
  LeadGenerationStatusEventsApiData,
  LeadGenerationStatusEventsApiResponses,
  ReferralActivateApiData,
  ReferralActivateApiResponses,
  ReferralCreateApiData,
  ReferralCreateApiResponses,
  ReferralEligibilityApiData,
  ReferralEligibilityApiResponses,
  ReferralGetApiData,
  ReferralGetApiResponses,
  ReferralGetFromSlugApiData,
  ReferralGetFromSlugApiResponses,
  ReferralListApiData,
  ReferralListApiResponses,
  ReferralPatchApiData,
  ReferralPatchApiResponses,
  TireQuoteAdminNoteCreateApiData,
  TireQuoteAdminNoteCreateApiResponses,
  TireQuoteAdminNoteDeleteApiData,
  TireQuoteAdminNoteDeleteApiResponses,
  TireQuoteAdminNoteListApiData,
  TireQuoteAdminNoteListApiResponses,
  TireQuoteAdminNotePatchApiData,
  TireQuoteAdminNotePatchApiResponses
} from '@navio/nsdk';

import type { ApiInput } from '../../utils';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import { getCampaignClaimStatusFromCodeEndpoint, getCampaignFromCodeEndpoint } from './campaignFromCodeEndpoints';
import {
  deleteCampaignEndpoint,
  endCampaignEndpoint,
  patchCampaignEndpoint,
  patchCampaignMultipartEndpoint
} from './campaignWriteEndpoints';
import { couponUrls } from './couponUrls';
import { tireQuoteAdminNoteAttachmentUrls } from './tireQuoteAdminNoteAttachmentUrls';

export type { CampaignActivityRecord } from '@navio/nsdk';
export { getLeadTireOfferUrls } from './leadTireOffers';
export { getPartnerReferralUrls } from './partnerReferrals';
export type CampaignCustomerRecord = CampaignCustomerRecordList;

export const getCampaignUrls = {
  getPaginatedCampaigns: dataEndpoint<CampaignListApiData, CampaignListApiResponses>({
    getUrl: (queryParams) => apiUrl('campaigns/', queryParams)
  }),
  getPaginatedBrandCampaigns: dataEndpoint<CampaignBrandListApiData, CampaignBrandListApiResponses>({
    getUrl: ({ brandId, ...queryParams }) => apiUrl(`brands/${brandId}/campaigns/`, queryParams)
  }),
  getCampaign: dataEndpoint<CampaignGetApiData, CampaignGetApiResponses>({
    getUrl: ({ campaignId }) => apiUrl(`campaigns/${campaignId}/`)
  }),
  getPaginatedCampaignCustomers: dataEndpoint<CampaignCustomerListApiData, CampaignCustomerListApiResponses>({
    getUrl: ({ campaignId, ...queryParams }) => apiUrl(`campaigns/${campaignId}/customers/`, queryParams)
  }),
  getPaginatedCampaignActivities: dataEndpoint<CampaignActivityListApiData, CampaignActivityListApiResponses>({
    getUrl: ({ campaignId, ...queryParams }) => apiUrl(`campaigns/${campaignId}/activities/`, queryParams)
  }),
  postCampaign: responseEndpoint<
    CampaignCreateApiData,
    CampaignCreateApiResponses,
    ApiInput<CampaignCreateApiData> & { authCode?: string; image?: File | Blob | null }
  >({
    getUrl: ({ brandId }) => apiUrl(`brands/${brandId}/campaigns/`),
    getBody: ({ brandId: _brandId, image: _image, authCode, ...body }) => ({
      ...body,
      ...(authCode != null && authCode.length > 0 ? { authCode } : {})
    })
  }),
  ...couponUrls,
  postCampaignDiscountOverlaps: responseEndpoint<CampaignDiscountOverlapsApiData, CampaignDiscountOverlapsApiResponses>(
    {
      getUrl: ({ brandId }) => apiUrl(`brands/${brandId}/campaigns/discount-overlaps/`),
      getBody: ({ brandId: _brandId, ...body }) => body
    }
  ),
  patchCampaign: patchCampaignEndpoint,
  patchCampaignMultipart: patchCampaignMultipartEndpoint,
  deleteCampaign: deleteCampaignEndpoint,
  endCampaign: endCampaignEndpoint,
  getCampaignFromCode: getCampaignFromCodeEndpoint,
  getCampaignClaimStatusFromCode: getCampaignClaimStatusFromCodeEndpoint,
  postActivateCampaign: responseEndpoint<
    CampaignActivateApiData,
    CampaignActivateApiResponses,
    ApiInput<CampaignActivateApiData> & { authCode?: string }
  >({
    getUrl: ({ campaignId }) => apiUrl(`campaigns/${campaignId}/activate/`),
    getBody: ({ userGroupId, brandDomain, authCode }) => ({
      userGroupId,
      brandDomain,
      authCode: authCode ?? ''
    })
  }),
  postAssignCampaign: responseEndpoint<CampaignAssignApiData, CampaignAssignApiResponses>({
    getUrl: ({ campaignId }) => apiUrl(`campaigns/${campaignId}/assign/`),
    getBody: (data) => data
  }),
  getPaginatedReferrals: dataEndpoint<ReferralListApiData, ReferralListApiResponses>({
    getUrl: (queryParams) => apiUrl('referrals/', queryParams)
  }),
  getReferral: dataEndpoint<ReferralGetApiData, ReferralGetApiResponses>({
    getUrl: ({ referralId }) => apiUrl(`referrals/${referralId}/`)
  }),
  postReferral: responseEndpoint<ReferralCreateApiData, ReferralCreateApiResponses>({
    getUrl: () => apiUrl('referrals/'),
    getBody: (data) => data
  }),
  patchReferral: responseEndpoint<ReferralPatchApiData, ReferralPatchApiResponses>({
    getUrl: ({ referralId }) => apiUrl(`referrals/${referralId}/`),
    getBody: (data) => data
  }),
  getPaginatedCouponGroups: dataEndpoint<CouponGroupListApiData, CouponGroupListApiResponses>({
    getUrl: (queryParams) => apiUrl('coupon-groups/', queryParams)
  }),
  getCouponGroup: dataEndpoint<CouponGroupGetApiData, CouponGroupGetApiResponses>({
    getUrl: ({ couponGroupId }) => apiUrl(`coupon-groups/${couponGroupId}/`)
  }),
  postCouponGroup: responseEndpoint<CouponGroupCreateApiData, CouponGroupCreateApiResponses>({
    getUrl: () => apiUrl('coupon-groups/'),
    getBody: (data) => data
  }),
  patchCouponGroup: responseEndpoint<CouponGroupPatchApiData, CouponGroupPatchApiResponses>({
    getUrl: ({ couponGroupId }) => apiUrl(`coupon-groups/${couponGroupId}/`),
    getBody: (data) => data
  }),
  getPaginatedCouponTemplates: dataEndpoint<CouponTemplateListApiData, CouponTemplateListApiResponses>({
    getUrl: (queryParams) => apiUrl('coupon-templates/', queryParams)
  }),
  getCouponTemplate: dataEndpoint<CouponTemplateGetApiData, CouponTemplateGetApiResponses>({
    getUrl: ({ couponTemplateId }) => apiUrl(`coupon-templates/${couponTemplateId}/`)
  }),
  postCouponTemplate: responseEndpoint<CouponTemplateCreateApiData, CouponTemplateCreateApiResponses>({
    getUrl: () => apiUrl('coupon-templates/'),
    getBody: (data) => data
  }),
  patchCouponTemplate: responseEndpoint<CouponTemplatePatchApiData, CouponTemplatePatchApiResponses>({
    getUrl: ({ couponTemplateId }) => apiUrl(`coupon-templates/${couponTemplateId}/`),
    getBody: (data) => data
  }),
  getPaginatedCouponActivities: dataEndpoint<CouponActivityListApiData, CouponActivityListApiResponses>({
    getUrl: (queryParams) => apiUrl('coupon-activities/', queryParams)
  }),
  getReferralFromSlug: dataEndpoint<ReferralGetFromSlugApiData, ReferralGetFromSlugApiResponses>({
    getUrl: ({ slug }) => apiUrl(`referrals/from-slug/${slug}/`)
  }),
  getReferralEligibility: dataEndpoint<ReferralEligibilityApiData, ReferralEligibilityApiResponses>({
    getUrl: ({ referralId, ...queryParams }) => apiUrl(`referrals/${referralId}/eligibility/`, queryParams)
  }),
  postActivateReferral: responseEndpoint<ReferralActivateApiData, ReferralActivateApiResponses>({
    getUrl: ({ referralId }) => apiUrl(`referrals/${referralId}/activate/`),
    getBody: ({ userGroupId, brandDomain }) => ({ userGroupId, brandDomain })
  }),
  getPaginatedLeadGenerationLeads: dataEndpoint<LeadGenerationListApiData, LeadGenerationListApiResponses>({
    getUrl: (queryParams) => apiUrl('lead-generations/', queryParams)
  }),
  getLeadGenerationLeads: dataEndpoint<LeadGenerationListApiData, LeadGenerationListApiResponses>({
    getUrl: (queryParams) => apiUrl('lead-generations/', queryParams)
  }),
  getLeadGenerationById: dataEndpoint<LeadGenerationGetApiData, LeadGenerationGetApiResponses>({
    getUrl: ({ leadGenerationId }) => apiUrl(`lead-generations/${leadGenerationId}/`)
  }),
  getLeadGenerationEvents: dataEndpoint<LeadGenerationStatusEventsApiData, LeadGenerationStatusEventsApiResponses>({
    getUrl: ({ leadGenerationId }) => apiUrl(`lead-generations/${leadGenerationId}/status-events/`)
  }),
  postLeadGenerationStatusEvent: responseEndpoint<
    LeadGenerationStatusEventCreateApiData,
    LeadGenerationStatusEventCreateApiResponses
  >({
    getUrl: ({ leadGenerationId }) => apiUrl(`lead-generations/${leadGenerationId}/status-events/`),
    getBody: (data) => data
  }),
  postApproveLeadGenerationBonus: responseEndpoint<
    LeadGenerationBonusApprovalCreateApiData,
    LeadGenerationBonusApprovalCreateApiResponses
  >({
    getUrl: ({ leadGenerationId }) => apiUrl(`lead-generations/${leadGenerationId}/bonus-approval/`),
    getBody: (data) => data
  }),
  postCreateLeadGeneration: responseEndpoint<LeadGenerationCreateApiData, LeadGenerationCreateApiResponses>({
    getUrl: () => apiUrl('lead-generations/'),
    getBody: (data) => data
  }),
  postAttachImageToLeadGeneration: responseEndpoint<
    LeadGenerationImageCreateApiData,
    LeadGenerationImageCreateApiResponses
  >({
    getUrl: ({ leadGenerationId }) => apiUrl(`lead-generations/${leadGenerationId}/images/`),
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    getBody: (data) => data,
    skipBodyTransformation: true
  }),
  getTireOfferAdminNotes: dataEndpoint<TireQuoteAdminNoteListApiData, TireQuoteAdminNoteListApiResponses>({
    getUrl: (queryParams) => apiUrl('car-tire-quote-admin-notes/', queryParams)
  }),
  postCreateTireOfferAdminNote: responseEndpoint<TireQuoteAdminNoteCreateApiData, TireQuoteAdminNoteCreateApiResponses>(
    {
      getUrl: () => apiUrl('car-tire-quote-admin-notes/'),
      getBody: (data) => data
    }
  ),
  patchTireOfferAdminNote: responseEndpoint<TireQuoteAdminNotePatchApiData, TireQuoteAdminNotePatchApiResponses>({
    getUrl: ({ tireQuoteAdminNoteId }) => apiUrl(`car-tire-quote-admin-notes/${tireQuoteAdminNoteId}/`),
    getBody: (data) => data
  }),
  deleteTireOfferAdminNote: dataEndpoint<TireQuoteAdminNoteDeleteApiData, TireQuoteAdminNoteDeleteApiResponses>({
    getUrl: ({ tireQuoteAdminNoteId }) => apiUrl(`car-tire-quote-admin-notes/${tireQuoteAdminNoteId}/`)
  }),
  ...tireQuoteAdminNoteAttachmentUrls,
  postCreateLead: responseEndpoint<LeadGenerationCreateApiData, LeadGenerationCreateApiResponses>({
    getUrl: () => apiUrl('lead-generations/'),
    getBody: (data) => data
  }),
  postAttachImageToStoneChipRepairLead: responseEndpoint<
    LeadGenerationImageCreateApiData,
    LeadGenerationImageCreateApiResponses,
    Omit<ApiInput<LeadGenerationImageCreateApiData>, 'image'> & {
      imageData: { uri: string; name: string; type: string };
    }
  >({
    getUrl: ({ leadGenerationId }) => apiUrl(`lead-generations/${leadGenerationId}/images/`),
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    skipBodyTransformation: true,
    getBody: ({ imageData, position }) => {
      const formData = new FormData();
      // @ts-expect-error React Native image shape for multipart
      formData?.append('image', imageData);
      // The tire of a new tires lead the photo shows; omitted for a photo of the whole lead.
      if (position) {
        formData.append('position', position);
      }

      return formData;
    }
  }),
  getTireOfferLeads: dataEndpoint<CarWheelMeasurementMinimumListApiData, CarWheelMeasurementMinimumListApiResponses>({
    getUrl: (queryParams) => apiUrl('car-wheel-measurements/minimum/', queryParams)
  })
};
